import { ROTULO_RECURSO_AUDITADO, type EventoDeAuditoria } from '@arena/shared/schemas';
import { adicionarDias, hojeNoFuso, partesNoFuso } from '@arena/shared/tempo';

/**
 * Formatação de `#v-acessos` e do console de auditoria.
 *
 * Funções puras, sem componente — o mesmo padrão de `src/agenda/formatos.ts`.
 */

/** A classe de cor da célula da matriz — mesma leitura do protótipo (`cel()` em app.js). */
export function classeDaCelula(nivel: string): string {
  if (nivel === 'total') return 's-ok';
  if (nivel === 'nao') return 's-off';
  return 's-wait';
}

const ROTULO_ACAO: Record<string, string> = {
  Leitura: 'abriu',
  Criacao: 'criou',
  Alteracao: 'alterou',
  Exclusao: 'excluiu',
  Anonimizacao: 'anonimizou',
  Negacao: 'tentou (negado)',
};

/** "Camila abriu o registro de Brenda Loureiro" / "Rafael alterou Configurações da arena". */
export function tituloDoEvento(evento: EventoDeAuditoria): string {
  const autor = evento.autor?.nome ?? 'Alguém sem sessão';
  const verbo = ROTULO_ACAO[evento.acao] ?? evento.acao.toLowerCase();
  const alvo = evento.pessoa
    ? `o registro de ${evento.pessoa.nome}`
    : ((ROTULO_RECURSO_AUDITADO as Record<string, string>)[evento.recurso] ?? evento.recurso);
  return `${autor} ${verbo} ${alvo}`;
}

/** A cor do selo de ação na tabela do console. */
export function classeDaAcao(acao: string): string {
  switch (acao) {
    case 'Negacao':
      return 's-late';
    case 'Exclusao':
    case 'Anonimizacao':
      return 's-warn';
    case 'Criacao':
      return 's-ok';
    default:
      return 's-off';
  }
}

/** "hoje 14:22" · "ontem 11:07" · "12/03 09:00" — como o feed do protótipo. */
export function quandoLocal(iso: string, fuso: string): string {
  const partes = partesNoFuso(iso, fuso);
  const hoje = hojeNoFuso(fuso);
  if (partes.data === hoje) return `hoje ${partes.hora}`;
  if (partes.data === adicionarDias(hoje, -1)) return `ontem ${partes.hora}`;
  const [, mes, dia] = partes.data.split('-');
  return `${dia}/${mes} ${partes.hora}`;
}

/** Um resumo de uma linha do `detalhe` — sem despejar o JSON inteiro na tela. */
export function resumoDoDetalhe(detalhe: Readonly<Record<string, unknown>> | null): string {
  if (!detalhe) return '—';
  const { de, para } = detalhe as { de?: unknown; para?: unknown };
  if (de !== undefined || para !== undefined) {
    const txt = (v: unknown) => (typeof v === 'object' ? JSON.stringify(v) : String(v ?? '—'));
    return `${txt(de)} → ${txt(para)}`;
  }
  if (typeof detalhe['tipo'] === 'string') return String(detalhe['tipo']);
  const chaves = Object.keys(detalhe);
  return chaves.length ? chaves.join(', ') : '—';
}
